// src/pages/SettingsPage.js
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MainNavbar from '../../components/MainNavbar';
import MainFooter from '../../components/MainFooter';
import ActionButton from '../../components/common/ActionButton';
import { useAuth } from '../../firebase/useAuth';
import { useToast } from '../../context/ToastContext';
import { useModal } from '../../context/ModalContext';
import { auth } from '../../firebaseConfig';
import { updateUserSettings } from '../../firebase/user';
import { reauthenticateWithCredential, EmailAuthProvider, sendEmailVerification, updatePassword } from 'firebase/auth';
import axios from 'axios';
import { backendURL } from '../../App';
import moment from 'moment-timezone';

// Import Icons
import {
  UserCircleIcon,
  KeyIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  AcademicCapIcon,
  Cog8ToothIcon,
} from '@heroicons/react/24/solid';

// Import New Components
import Spinner from '../../components/common/Spinner';
import FormField from '../../components/common/FormField';
import ToggleSwitch from '../../components/common/ToggleSwitch';
import SettingsSlider from '../../components/settings/Slider';
import CustomCheckbox from '../../components/common/CustomCheckbox';
import EnhancedSelect from '../../components/common/EnhancedSelect';
import GlassyIcon from '../../components/common/GlassyIcon';
import GlassyBadge from '../../components/common/GlassyBadge';
import SessionSettingsModal from '../../components/modals/SessionSettingsModal';
import DangerZone from '../../components/settings/DangerZone';

// Settings Card with glassmorphic background and sound bars
const SettingsCard = ({ title, icon, children, gradientFrom, gradientTo, className = '', decorations = true }) => (
  <div className={`relative rounded-2xl backdrop-blur-sm border-2 border-border overflow-hidden group animate-fade-in ${className}`}>
    {/* Glassmorphic background with gradient */}
    <div
      className="absolute inset-0"
      style={{
        background: `linear-gradient(135deg, ${gradientFrom}10, ${gradientTo}15)`,
      }}
    />

    {/* Enhanced glassmorphic overlay */}
    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30" />

    {/* Audio bars in corners */}
    {decorations && (
    <div className="absolute inset-0 opacity-20 dark:opacity-15">
      {/* Top-right corner audio bars */}
      <div className="absolute top-4 right-4 flex gap-1 group-hover:scale-110 transition-transform duration-700">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="rounded-full"
            style={{
              width: '3px',
              height: `${10 + i * 3}px`,
              background: `linear-gradient(180deg, ${gradientFrom}, ${gradientTo})`,
              animation: `audio-bar ${2 + i * 0.3}s ease-in-out infinite alternate`,
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>

      {/* Bottom-left corner audio bars */}
      <div className="absolute bottom-1 left-4 flex gap-1 group-hover:scale-110 transition-transform duration-700">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="rounded-full"
            style={{
              width: '2px',
              height: `${8 + i * 2}px`,
              background: `linear-gradient(180deg, ${gradientFrom}, ${gradientTo})`,
              animation: `audio-bar ${2.4 + i * 0.3}s ease-in-out infinite alternate`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>
    </div>
    )}

    <div className="relative z-10 p-6">
      {/* Header with icon */}
      <div className="flex items-center gap-3 mb-6">
        {icon}
        <h3 className="text-xl font-bold text-content-primary drop-shadow-sm">{title}</h3>
      </div>

      {/* Content */}
      <div className="space-y-4">{children}</div>
    </div>
  </div>
);

// Enhanced Setting Row with better spacing and styling
const SettingRow = ({ title, description, children, className = '' }) => (
  <div className={`py-4 sm:grid sm:grid-cols-3 sm:gap-6 sm:py-5 border-b border-border last:border-b-0 ${className}`}>
    <div className="flex flex-col">
      <label className="text-sm font-semibold text-content-primary drop-shadow-sm">{title}</label>
      {description && <p className="mt-1 text-xs text-content-secondary opacity-80 leading-relaxed">{description}</p>}
    </div>
    <div className="mt-3 sm:col-span-2 sm:mt-0">{children}</div>
  </div>
);

// Helper function to generate better timezone options with GMT offsets
const getTimezoneOptions = () => {
  const now = moment();
  const popularTimezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Rome',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Kolkata',
    'Australia/Sydney',
    'Pacific/Auckland',
  ];

  // Add UTC at the beginning
  const utcOption = {
    value: 'UTC',
    label: '(GMT+0) UTC',
    isPopular: true,
    offset: 0,
  };

  const allTimezones = moment.tz
    .names()
    .filter((tz) => {
      // Filter out deprecated and weird timezone names, but keep UTC
      if (tz === 'UTC') return false; // We'll add UTC manually at the top
      return (
        !tz.includes('Etc/') &&
        !tz.includes('SystemV/') &&
        !tz.includes('US/') &&
        tz.includes('/') &&
        !tz.startsWith('GMT') &&
        !tz.startsWith('UCT')
      );
    })
    .map((tz) => {
      const offsetHours = moment.tz(now, tz).utcOffset() / 60;
      const sign = offsetHours >= 0 ? '+' : '';
      const displayName = tz.replace(/_/g, ' ').replace('/', ' / ');

      return {
        value: tz,
        label: `(GMT${sign}${offsetHours}) ${displayName}`,
        isPopular: popularTimezones.includes(tz),
        offset: offsetHours,
      };
    })
    .sort((a, b) => {
      // Sort popular timezones first, then by offset
      if (a.isPopular && !b.isPopular) return -1;
      if (!a.isPopular && b.isPopular) return 1;
      if (a.isPopular && b.isPopular) return a.offset - b.offset;
      return a.offset - b.offset;
    });

  // Add UTC at the beginning of the list
  return [utcOption, ...allTimezones];
};

function SettingsPage() {
  const { userId, userData, subscriptionStatus } = useAuth();
  const { showToast } = useToast();
  const { showModal, hideModal } = useModal();
  const navigate = useNavigate();

  const [formData, setFormData] = useState(null);
  const [loadingStates, setLoadingStates] = useState({
    settings: false,
    password: false,
    verification: false,
  });

  // Prevent navigation while settings are being saved
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (loadingStates.settings) {
        const message = 'Settings are still being saved. Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    // Override navigation functions to show confirmation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    const interceptNavigation = () => {
      if (loadingStates.settings) {
        const shouldProceed = window.confirm('Settings are still being saved. Are you sure you want to leave this page?');
        return shouldProceed;
      }
      return true;
    };

    window.history.pushState = function(...args) {
      if (interceptNavigation(args[2])) {
        originalPushState.apply(window.history, args);
      }
    };

    window.history.replaceState = function(...args) {
      if (interceptNavigation(args[2])) {
        originalReplaceState.apply(window.history, args);
      }
    };

    // Also handle clicks on navigation elements
    const handleClick = (event) => {
      if (loadingStates.settings) {
        const target = event.target.closest('a[href], button[data-to]');
        if (target && (target.href || target.dataset.to)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          
          const shouldProceed = window.confirm('Settings are still being saved. Are you sure you want to leave this page?');
          if (shouldProceed) {
            if (target.href && target.href.startsWith(window.location.origin)) {
              const path = target.href.replace(window.location.origin, '');
              navigate(path);
            } else if (target.dataset.to) {
              navigate(target.dataset.to);
            }
          }
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handleClick, true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleClick, true);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [loadingStates.settings, navigate]);

  useEffect(() => {
    if (userData && !formData) {
      const settings = userData.settings || {};
      setFormData({
        sentencesPerDay: settings.sentences_per_day ?? 5,
        sentenceReviewsPerDay: settings.sentence_reviews_per_day ?? -1,
        wordsReviewPerDay: settings.words_reviews_per_day ?? -1,
        sentenceUnlocking: settings.sentence_unlocking || 'level',
        wkLevelMin: settings.wk_level_min ?? '',
        wkLevelMax: settings.wk_level_max ?? '',
        reviewBurnedItems: settings.review_burned_items || false,
        getWeeklyReports: settings.get_weekly_reports !== undefined ? settings.get_weekly_reports : true,
        timezone: settings.timezone || 'UTC',
        reviewFrequency: settings.review_frequency || 'day',
        vacationStartedAt: settings.vacation_started_at || null,
        levelsToLearnFrom: Array.isArray(settings.levels_to_learn_from) ? settings.levels_to_learn_from : [5, 4, 3, 2, 1],
      });
    }
  }, [userData, formData]);

  const handleChange = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleLevelsChange = (level) => {
    const currentLevels = formData.levelsToLearnFrom || [];
    if (currentLevels.length === 1 && currentLevels[0] === level) {
      showToast('You must select at least one level.', 'error');
      return;
    }
    const newLevels = currentLevels.includes(level)
      ? currentLevels.filter((l) => l !== level)
      : [...new Set([...currentLevels, level])].sort((a, b) => b - a);
    handleChange('levelsToLearnFrom', newLevels);
  };

  const handleSettingsSave = async () => {
    setLoadingStates((prev) => ({ ...prev, settings: true }));

    if (!subscriptionStatus.isSubscribed && formData.sentenceUnlocking === 'random') {
      showToast('Random sentence unlocking requires Premium.', 'error');
      setLoadingStates((prev) => ({ ...prev, settings: false }));
      return;
    }
    if (!subscriptionStatus.isSubscribed && formData.sentenceUnlocking === 'wanikani') {
      showToast('Kanji Level sentence unlocking requires Premium.', 'error');
      setLoadingStates((prev) => ({ ...prev, settings: false }));
      return;
    }
    if (!subscriptionStatus.isSubscribed && !formData.levelsToLearnFrom.includes(5)) {
      showToast('Free users must include Level 5.', 'error');
      setLoadingStates((prev) => ({ ...prev, settings: false }));
      return;
    }

    const coerceWkLevel = (val) => {
      if (val === '' || val == null) return null;
      const n = parseInt(val, 10);
      return n >= 0 && n <= 61 ? n : null;
    };

    {
      const min = formData.wkLevelMin !== '' && formData.wkLevelMin != null ? parseInt(formData.wkLevelMin, 10) : null;
      const max = formData.wkLevelMax !== '' && formData.wkLevelMax != null ? parseInt(formData.wkLevelMax, 10) : null;
      if (min != null && (isNaN(min) || min < 0 || min > 61)) {
        showToast('WaniKani "From" level must be between 0 and 61.', 'error');
        setLoadingStates((prev) => ({ ...prev, settings: false }));
        return;
      }
      if (max != null && (isNaN(max) || max < 0 || max > 61)) {
        showToast('WaniKani "Up to" level must be between 0 and 61.', 'error');
        setLoadingStates((prev) => ({ ...prev, settings: false }));
        return;
      }
      if (min != null && max != null && min > max) {
        showToast('WaniKani "From" level cannot be greater than "Up to" level.', 'error');
        setLoadingStates((prev) => ({ ...prev, settings: false }));
        return;
      }
    }

    try {
      const turningOffVacation = !formData.vacationStartedAt && userData?.settings?.vacation_started_at;
      const settingsToSave = {
        ...formData,
        wkLevelMin: coerceWkLevel(formData.wkLevelMin),
        wkLevelMax: coerceWkLevel(formData.wkLevelMax),
      };
      await updateUserSettings(userId, settingsToSave);

      if (turningOffVacation) {
        showToast('Settings saved. Rescheduling your reviews...', 'info');
        await axios.post(`${backendURL}/shiftAllReviews`, {
          userId,
          vacationStartedAt: userData.settings.vacation_started_at,
        });
        showToast('Settings saved and reviews rescheduled!', 'success');
      } else {
        showToast('Settings saved successfully!', 'success');
      }
    } catch (err) {
      showToast('Error saving settings. Please try again.', 'error');
    } finally {
      setLoadingStates((prev) => ({ ...prev, settings: false }));
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmPassword } = formData;
    if (newPassword !== confirmPassword) {
      showToast('New passwords do not match.', 'error');
      return;
    }
    setLoadingStates((prev) => ({ ...prev, password: true }));
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);
      showToast('Password updated successfully!', 'success');
      handleChange('currentPassword', '');
      handleChange('newPassword', '');
      handleChange('confirmPassword', '');
    } catch (error) {
      showToast('Error updating password. Check your current password.', 'error');
    } finally {
      setLoadingStates((prev) => ({ ...prev, password: false }));
    }
  };

  const handleResendVerification = async () => {
    setLoadingStates((prev) => ({ ...prev, verification: true }));
    try {
      await sendEmailVerification(auth.currentUser);
      showToast('Verification email sent! Please check your inbox.', 'success');
    } catch (error) {
      if (error.code === 'auth/too-many-requests') {
        showToast('Email was already sent recently. Please check your inbox and spam folder, or try again in a few minutes.', 'info');
      } else {
        showToast('Error sending email. Please try again later.', 'error');
      }
    } finally {
      setLoadingStates((prev) => ({ ...prev, verification: false }));
    }
  };

  if (!formData) {
    return (
      <div className="flex flex-col min-h-screen bg-background-primary">
        <MainNavbar />
        <div className="flex-grow flex items-center justify-center">
          <Spinner />
        </div>
        <MainFooter />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background-primary">
      <MainNavbar />

      <main className="flex-grow max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-8">
          {/* User Information Card */}
          <SettingsCard
            title="User Information"
            icon={<GlassyIcon icon={UserCircleIcon} size="md" color="orange" />}
            gradientFrom="var(--color-primary)"
            gradientTo="var(--color-secondary)"
          >
            {/* Email Verification Alert */}
            {auth.currentUser && !auth.currentUser.emailVerified && (
              <div className="mb-6 p-4 rounded-xl backdrop-blur-sm border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-orange-500/10">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-grow">
                    <p className="text-sm font-medium text-content-primary mb-3">Please verify your email to enjoy full access.</p>
                    <ActionButton
                      onClick={handleResendVerification}
                      loading={loadingStates.verification}
                      variant="glassy"
                      color="amber"
                      className="text-sm px-4 py-2 font-medium"
                    >
                      Resend Verification Email
                    </ActionButton>
                  </div>
                </div>
              </div>
            )}

            {/* User Info Display */}
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-background-secondary/50 border border-border">
                <div>
                  <p className="text-sm font-medium text-content-secondary">Email Address</p>
                  <p className="text-base font-semibold text-content-primary">{auth.currentUser?.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  {auth.currentUser?.emailVerified ? (
                    <GlassyBadge icon={CheckCircleIcon} color="green" size="sm">
                      Verified
                    </GlassyBadge>
                  ) : (
                    <GlassyBadge icon={ExclamationTriangleIcon} color="amber" size="sm">
                      Not Verified
                    </GlassyBadge>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg bg-background-secondary/50 border border-border">
                <div>
                  <p className="text-sm font-medium text-content-secondary">Member Since</p>
                  <p className="text-base font-semibold text-content-primary">
                    {new Date(auth.currentUser?.metadata?.creationTime).toLocaleDateString()}
                  </p>
                </div>
                <GlassyBadge icon={SparklesIcon} color="orange" size="sm">
                  Member
                </GlassyBadge>
              </div>
            </div>
          </SettingsCard>

          {/* Learning, Reviews & Schedule Card */}
          <SettingsCard
            title="Learning, Reviews & Schedule"
            icon={<GlassyIcon icon={AcademicCapIcon} size="md" color="green" />}
            gradientFrom="var(--color-session-learn)"
            gradientTo="var(--color-session-learn-accent)"
          >
            <div className="space-y-4">
              <SettingRow title="New Sentences / Day" description="Number of new sentences to learn each day.">
                <div className="flex items-center gap-4">
                  <div className="flex-grow">
                    <SettingsSlider
                      min={1}
                      max={30}
                      value={formData.sentencesPerDay}
                      onChange={(value) => handleChange('sentencesPerDay', value)}
                    />
                  </div>
                  <div
                    className="w-12 h-8 rounded-lg flex items-center justify-center font-bold text-white text-sm"
                    style={{
                      background: 'var(--color-session-learn)',
                      boxShadow: '0 2px 8px var(--color-session-learn)40',
                    }}
                  >
                    {formData.sentencesPerDay}
                  </div>
                </div>
              </SettingRow>

              <SettingRow title="Max Sentence Reviews / Day" description="Maximum reviews per day. 'UL' means unlimited.">
                <div className="flex items-center gap-4">
                  <div className="flex-grow">
                    <SettingsSlider
                      min={-1}
                      max={300}
                      value={formData.sentenceReviewsPerDay}
                      onChange={(value) => handleChange('sentenceReviewsPerDay', value)}
                    />
                  </div>
                  <div
                    className="w-12 h-8 rounded-lg flex items-center justify-center font-bold text-white text-sm"
                    style={{
                      background: 'var(--color-session-review)',
                      boxShadow: '0 2px 8px var(--color-session-review)40',
                    }}
                  >
                    {formData.sentenceReviewsPerDay === -1 ? 'UL' : formData.sentenceReviewsPerDay}
                  </div>
                </div>
              </SettingRow>

              <SettingRow title="Max Word Reviews / Day" description="Maximum word reviews per day. 'UL' means unlimited.">
                <div className="flex items-center gap-4">
                  <div className="flex-grow">
                    <SettingsSlider
                      min={-1}
                      max={300}
                      value={formData.wordsReviewPerDay}
                      onChange={(value) => handleChange('wordsReviewPerDay', value)}
                    />
                  </div>
                  <div
                    className="w-12 h-8 rounded-lg flex items-center justify-center font-bold text-white text-sm"
                    style={{
                      background: 'var(--color-words)',
                      boxShadow: '0 2px 8px var(--color-words)40',
                    }}
                  >
                    {formData.wordsReviewPerDay === -1 ? 'UL' : formData.wordsReviewPerDay}
                  </div>
                </div>
              </SettingRow>

              <SettingRow title="Sentence Unlocking" description="Order for new sentences. Kanji Level and Random require Premium.">
                <EnhancedSelect
                  value={formData.sentenceUnlocking}
                  onChange={(e) => handleChange('sentenceUnlocking', e.target.value)}
                  options={[
                    { value: 'level', label: 'Level-based (Highest First)' },
                    {
                      value: 'wanikani',
                      label: `Kanji Level${!subscriptionStatus.isSubscribed ? ' (Premium)' : ''}`,
                      disabled: !subscriptionStatus.isSubscribed,
                    },
                    {
                      value: 'random',
                      label: `Randomly${!subscriptionStatus.isSubscribed ? ' (Premium)' : ''}`,
                      disabled: !subscriptionStatus.isSubscribed,
                    },
                  ]}
                />
              </SettingRow>

              <SettingRow title="Learn From Levels" description="JLPT levels for new sentences. Free users must include N5.">
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-x-2 sm:gap-x-4 gap-y-2">
                    {[5, 4, 3, 2, 1].map((level) => (
                      <CustomCheckbox
                        key={level}
                        checked={formData.levelsToLearnFrom.includes(level)}
                        onChange={() => handleLevelsChange(level)}
                        disabled={!subscriptionStatus.isSubscribed && level !== 5}
                        label={`N${level}`}
                        className="text-sm sm:text-base"
                      />
                    ))}
                  </div>
                  {!subscriptionStatus.isSubscribed && (
                    <p className="text-sm text-content-secondary">
                      Unlock all levels with{' '}
                      <Link to="/user/subscription" className="font-semibold text-primary hover:text-secondary transition-colors duration-200">
                        Premium
                      </Link>
                      .
                    </p>
                  )}
                </div>
              </SettingRow>

              <SettingRow
                  title="WaniKani Level Range"
                  description="0 = kana only, 1–60 = WaniKani levels, 61 = non-WK kanji. Leave blank for no restriction."
                >
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-content-secondary mb-1">From level</label>
                      <input
                        type="number"
                        min="0"
                        max="61"
                        value={formData.wkLevelMin}
                        onChange={(e) => handleChange('wkLevelMin', e.target.value)}
                        onKeyDown={(e) => {
                          const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                          if (allowed.includes(e.key)) return;
                          if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
                          if (!/^\d$/.test(e.key)) e.preventDefault();
                        }}
                        placeholder="0"
                        className="w-full px-3 py-2 rounded-lg border-2 border-border text-content-primary font-medium text-sm hover:border-primary/50 transition-all duration-200 outline-none focus:border-primary [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        style={{ background: 'var(--color-background-secondary)', MozAppearance: 'textfield' }}
                      />
                    </div>
                    <span className="text-content-secondary pb-2">–</span>
                    <div className="flex-1">
                      <label className="block text-xs text-content-secondary mb-1">Up to level</label>
                      <input
                        type="number"
                        min="0"
                        max="61"
                        value={formData.wkLevelMax}
                        onChange={(e) => handleChange('wkLevelMax', e.target.value)}
                        onKeyDown={(e) => {
                          const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                          if (allowed.includes(e.key)) return;
                          if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
                          if (!/^\d$/.test(e.key)) e.preventDefault();
                        }}
                        placeholder="61"
                        className="w-full px-3 py-2 rounded-lg border-2 border-border text-content-primary font-medium text-sm hover:border-primary/50 transition-all duration-200 outline-none focus:border-primary [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        style={{ background: 'var(--color-background-secondary)', MozAppearance: 'textfield' }}
                      />
                    </div>
                  </div>
                </SettingRow>

              <SettingRow title="Review Burned Items" description="Occasionally review items you've fully mastered.">
                <ToggleSwitch enabled={formData.reviewBurnedItems} setEnabled={(value) => handleChange('reviewBurnedItems', value)} />
              </SettingRow>

              <SettingRow title="Review Availability" description="How often the system checks for new reviews.">
                <EnhancedSelect
                  value={formData.reviewFrequency}
                  onChange={(e) => handleChange('reviewFrequency', e.target.value)}
                  options={[
                    { value: 'day', label: 'Daily (Recommended)' },
                    { value: 'hour', label: 'Hourly' },
                  ]}
                />
              </SettingRow>

              <SettingRow title="Weekly Reports" description="Receive an email summary of your progress each week.">
                <ToggleSwitch enabled={formData.getWeeklyReports} setEnabled={(value) => handleChange('getWeeklyReports', value)} />
              </SettingRow>

              <SettingRow title="Timezone" description="Affects daily reset times for lessons and reviews.">
                <EnhancedSelect
                  value={formData.timezone}
                  onChange={(e) => handleChange('timezone', e.target.value)}
                  options={getTimezoneOptions()}
                  className="max-w-sm"
                />
              </SettingRow>

              <SettingRow title="Vacation Mode" description="Pause all reviews. They will be rescheduled when you return.">
                <ToggleSwitch
                  enabled={!!formData.vacationStartedAt}
                  setEnabled={(value) => handleChange('vacationStartedAt', value ? new Date().toISOString() : null)}
                />
              </SettingRow>
            </div>

            {/* Save Button */}
            <div className="mt-8 pt-6 border-t border-border flex justify-end">
              <ActionButton
                onClick={handleSettingsSave}
                loading={loadingStates.settings}
                variant="glassy"
                color="orange"
                className="px-6 py-3 font-semibold"
              >
                Save Settings
              </ActionButton>
            </div>
          </SettingsCard>

          {/* Session Settings Card */}
          <SettingsCard
            title="Session Settings"
            icon={<GlassyIcon icon={Cog8ToothIcon} size="md" color="purple" />}
            gradientFrom="var(--color-secondary)"
            gradientTo="var(--color-stat-accuracy)"
          >
            <p className="text-sm text-content-secondary leading-relaxed mb-4">
              Configure how your study sessions work — input mode (Kanji vs Kana), sounds, animations, and more.
            </p>
            <div className="flex justify-center">
              <ActionButton
                type="button"
                variant="glassy"
                color="purple"
                className="px-6 py-3 font-semibold"
                onClick={() => showModal(<SessionSettingsModal onClose={hideModal} updateSettings={() => {}} />)}
              >
                Open Session Settings
              </ActionButton>
            </div>
          </SettingsCard>

          {/* Password Settings Card */}
          <SettingsCard
            title="Change Password"
            icon={<GlassyIcon icon={KeyIcon} size="md" color="blue" />}
            gradientFrom="var(--color-secondary)"
            gradientTo="var(--color-accent)"
          >
            <form onSubmit={handlePasswordChange} className="space-y-6">
              <div className="space-y-4">
                <FormField
                  id="currentPassword"
                  label="Current Password"
                  type="password"
                  value={formData.currentPassword || ''}
                  onChange={(e) => handleChange('currentPassword', e.target.value)}
                  required
                />
                <FormField
                  id="newPassword"
                  label="New Password"
                  type="password"
                  value={formData.newPassword || ''}
                  onChange={(e) => handleChange('newPassword', e.target.value)}
                  required
                />
                <FormField
                  id="confirmPassword"
                  label="Confirm New Password"
                  type="password"
                  value={formData.confirmPassword || ''}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  required
                />
              </div>

              <div className="flex justify-end pt-4 border-t border-border">
                <ActionButton
                  type="submit"
                  loading={loadingStates.password}
                  variant="glassy"
                  color="blue"
                  className="px-6 py-3 font-semibold"
                >
                  Update Password
                </ActionButton>
              </div>
            </form>
          </SettingsCard>

          {/* Danger Zone Card */}
          <SettingsCard
            title="Danger Zone"
            icon={<GlassyIcon icon={ExclamationTriangleIcon} size="md" color="red" />}
            gradientFrom="var(--color-glassy-red)"
            gradientTo="var(--color-glassy-red-to)"
            decorations={false}
            className="!border-red-500/40"
          >
            <DangerZone />
          </SettingsCard>
        </div>
      </main>

      <MainFooter />
    </div>
  );
}

export default SettingsPage;
